/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Component,
  Directive,
  Host,
  ViewEncapsulation,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import {SPACE, ENTER} from '../core/keyboard/keycodes';
import {MdExpansionPanel, EXPANSION_PANEL_ANIMATION_TIMING} from './expansion-panel';
/**
 * <md-expansion-panel-header> component.
 * 
 * This component corresponds to the header element of an <md-expansion-panel>.
 * 
 * Please refer to README.md for examples on how to use it.
 */
export class MdExpansionPanelHeader {
/**
 * @param {?} panel
 */
constructor(
public panel: MdExpansionPanel) {}
/**
 * Toggles the expanded state of the panel.
 * @return {?}
 */
_toggle(): void {
    this.panel.toggle();
  }
/**
 * Gets whether the panel is expanded.
 * @return {?}
 */
_isExpanded(): boolean {
    return this.panel.expanded;
  }
/**
 * Gets the expanded state string of the panel.
 * @return {?}
 */
_getExpandedState(): string {
    return this.panel._getExpandedState();
  }
/**
 * Gets the panel id.
 * @return {?}
 */
_getPanelId(): string {
    return this.panel.id;
  }
/**
 * Gets whether the expand indicator is hidden.
 * @return {?}
 */
_getHideToggle(): boolean {
    return this.panel.hideToggle;
  }
/**
 * Handle keyup event calling to toggle() if appropriate.
 * @param {?} event
 * @return {?}
 */
_keyup(event: KeyboardEvent) {
    switch (event.keyCode) {
      // Toggle for space and enter keys.
      case SPACE:
      case ENTER:
        event.preventDefault();
        this._toggle();
        break;
      default:
        return;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-expansion-panel-header, mat-expansion-panel-header',
  styleUrls: ['./expansion-panel-header.css'],
  templateUrl: './expansion-panel-header.html',
  encapsulation: ViewEncapsulation.None,
  host: {
    'class': 'mat-expansion-panel-header',
    'role': 'button',
    'tabindex': '0',
    '[attr.aria-controls]': '_getPanelId()',
    '[attr.aria-expanded]': '_isExpanded()',
    '[class.mat-expanded]': '_isExpanded()',
    '(click)': '_toggle()',
    '(keyup)': '_keyup($event)',
    '[@expansionHeight]': '_getExpandedState()',
  },
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({transform: 'rotate(0deg)'})),
      state('expanded', style({transform: 'rotate(180deg)'})),
      transition('expanded <=> collapsed', animate(EXPANSION_PANEL_ANIMATION_TIMING)),
    ]),
    trigger('expansionHeight', [
      state('collapsed', style({height: '48px', 'line-height': '48px'})),
      state('expanded', style({height: '64px', 'line-height': '68px'})),
      transition('expanded <=> collapsed', animate(EXPANSION_PANEL_ANIMATION_TIMING)),
    ]),
  ],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdExpansionPanel, decorators: [{ type: Host }, ]},
];
}

function MdExpansionPanelHeader_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanelHeader.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanelHeader.ctorParameters;
/** @type {?} */
MdExpansionPanelHeader.prototype.panel;
}

/**
 * <md-panel-description> directive.
 * 
 * This direction is to be used inside of the MdExpansionPanelHeader component.
 */
export class MdExpansionPanelDescription {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-panel-description, mat-panel-description',
  host : {
    class: 'mat-expansion-panel-header-description'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdExpansionPanelDescription_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanelDescription.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanelDescription.ctorParameters;
}

/**
 * <md-panel-title> directive.
 * 
 * This direction is to be used inside of the MdExpansionPanelHeader component.
 */
export class MdExpansionPanelTitle {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-panel-title, mat-panel-title',
  host : {
    class: 'mat-expansion-panel-header-title'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdExpansionPanelTitle_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanelTitle.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanelTitle.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
